import React from 'react';
import { trackError } from './error-tracking';

// Survives a single deploy: after a successful reload, the new bundle's chunk
// hashes match what code requests, so we won't trip again. The timestamp guard
// stops infinite reloads if a chunk is genuinely missing (not just stale).
const RELOAD_KEY = 'lazyWithRetry:lastReloadAt';
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /MIME type [^"]*text\/html/i,
];

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === 'string'
        ? err
        : '';
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(msg));
}

function recentlyReloaded(): boolean {
  try {
    const ts = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
    return ts > 0 && Date.now() - ts < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloadAndRefresh(reason: string): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable (private mode, quota); fall through
  }
  trackError('chunk_load_reload', new Error(reason), {
    component: 'lazyWithRetry',
    action: 'reload',
    extra: { url: window.location.href },
  });
  window.location.reload();
}

export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err) && !recentlyReloaded()) {
        markReloadAndRefresh(err instanceof Error ? err.message : String(err));
        // Reload is async; return a never-resolving promise so React keeps the
        // Suspense fallback up instead of throwing the error to the boundary.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

// Catches chunk-load failures that happen outside React.lazy: Vite's module
// preload, eager imports during route transitions, etc. Call once at boot.
export function installChunkErrorHandler(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('vite:preloadError', (event: Event) => {
    if (recentlyReloaded()) return;
    const detail = (event as CustomEvent).detail;
    const msg = detail instanceof Error ? detail.message : 'vite:preloadError';
    event.preventDefault();
    markReloadAndRefresh(msg);
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    if (!isChunkLoadError(event.error || event.message)) return;
    if (recentlyReloaded()) return;
    markReloadAndRefresh(event.message || 'window.error chunk load');
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    if (recentlyReloaded()) return;
    const msg =
      event.reason instanceof Error ? event.reason.message : String(event.reason);
    markReloadAndRefresh(msg);
  });
}
